import React, { useState, useEffect, useRef, useCallback } from 'react';
import {logout} from '../utils/authUtils';
import { getNotificationList,dismissAllNotifications,markNotificationRead } from '../services/notification.service';
import * as CryptoJS from 'crypto-js';
import './Notificationbell.css';

function NotificationBell() {
    const [open, setOpen]                   = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount]     = useState(0);
    const [loading, setLoading]             = useState(false);

    // Accordion state: which sections are expanded
    const [expanded, setExpanded] = useState({ today: true, last7: true, last814: true });

    const panelRef = useRef(null);
    const bellRef  = useRef(null);
    const handleLogout = (event) => {
        event.preventDefault();
        logout();
    };
    /* ── fetch notifications ── */
    const fetchNotifications = useCallback(async () => {
        try { setLoading(true);
            //encrypted data
            let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
                //'param_data':obj,
                'authToken': sessionStorage.getItem('LoggedInToken'),
            }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
            let oData = {'token':EncryptData}
            getNotificationList(oData).then(res=>{
                //console.log(res.data);
                if(res.data?.error?.name =="TokenExpiredError"){ 
                    handleLogout(); 
                }else{
                if(res?.data?.data?.length) setNotifications(res.data.data);
                    //setCount(res?.data?.data_count[0]?.total_notify);
                    //const unread = res.data.data.filter((n) => !n.is_read).length;
                    setUnreadCount(res?.data?.data_count[0]?.total_notify);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
          //setNotifications(response.data);
          
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      },[]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000); // refresh every 60s
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    /* ── close on outside click ── */
    useEffect(() => {
        const handleOutside = (e) => {
            if (
                open &&
                panelRef.current &&
                !panelRef.current.contains(e.target) &&
                bellRef.current &&
                !bellRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    /* ── group notifications by date bucket ── */
    const groupNotifications = (items) => {
        const now   = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const groups = { today: [], last7: [], last814: [] };

        items.forEach((n) => {
            const d    = new Date(n.created_at || n.notification_date || n.date);
            const diff = Math.floor((today - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);

            if (diff <= 0)        groups.today.push(n);
            else if (diff <= 7)   groups.last7.push(n);
            else if (diff <= 14)  groups.last814.push(n);
        });

        return groups;
    };

    const groups = groupNotifications(notifications);

    /* ── dismiss all ── */
    const handleDismissAll = () => {
        setNotifications([]);
        setUnreadCount(0);
    };

    /* ── toggle section ── */
    const toggleSection = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    /* ── render one notification item ── */
    const renderItem = (n, idx) => (
        <div
            key={n.id || idx}
            id={`nbell-item-${n.id || idx}`}
            className={`nbell-item${n.is_read ? '' : ' nbell-item--unread'}`}
        >
            <div className="nbell-item-dot" aria-hidden="true"></div>
            <div className="nbell-item-body">
                <p className="nbell-item-message">{n.message || n.notification_message || n.title}</p>
                {n.sub_message && (
                    <p className="nbell-item-sub">{n.sub_message}</p>
                )}
                <span className="nbell-item-time">
                    <i className="bi bi-clock"></i>
                    {n.time_label || formatTime(n.created_at || n.notification_date)}
                </span>
            </div>
        </div>
    );

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
        } catch { return ''; }
    };

    /* ── render collapsible group section ── */
    const renderSection = (label, key, items) => (
        <div className="nbell-group" id={`nbell-group-${key}`} key={key}>
            <button
                className={`nbell-group-header${expanded[key] ? ' nbell-group-header--open' : ''}`}
                id={`nbell-group-btn-${key}`}
                onClick={() => toggleSection(key)}
                type="button"
            >
                <i className={`bi bi-chevron-${expanded[key] ? 'down' : 'right'} nbell-chevron`}></i>
                <span className="nbell-group-label">{label}</span>
                {items.length > 0 && (
                    <span className="nbell-group-count">{items.length}</span>
                )}
            </button>

            {expanded[key] && (
                <div className="nbell-group-body" id={`nbell-group-body-${key}`}>
                    {items.length === 0 ? (
                        <p className="nbell-empty">No items to display.</p>
                    ) : (
                        items.map((n, idx) => renderItem(n, idx))
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="nbell-wrapper" id="nbell-wrapper">
            {/* ── Bell Button ── */}
            <button
                ref={bellRef}
                id="nbell-btn"
                className={`nbell-btn${open ? ' nbell-btn--active' : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-label="Notifications"
                type="button"
            >
                <i className="bi bi-bell-fill nbell-icon"></i>
                {unreadCount > 0 && (
                    <span className="nbell-badge" id="nbell-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* ── Notification Panel ── */}
            {open && (
                <div
                    ref={panelRef}
                    id="nbell-panel"
                    className="nbell-panel"
                    role="dialog"
                    aria-label="Notifications panel"
                >
                    {/* Panel Header */}
                    <div className="nbell-panel-header" id="nbell-panel-header">
                        <h3 className="nbell-panel-title" id="nbell-panel-title">
                            Notifications
                        </h3>
                        <button
                            id="nbell-close-btn"
                            className="nbell-close-btn"
                            onClick={() => setOpen(false)}
                            aria-label="Close notifications"
                            type="button"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Info Banner */}
                    <div className="nbell-info-banner" id="nbell-info-banner">
                        <div className="nbell-info-banner-left">
                            <i className="bi bi-info-circle-fill nbell-info-icon"></i>
                            <span>Notifications are automatically dismissed after 14 days.</span>
                        </div>
                        <button
                            id="nbell-dismiss-all"
                            className="nbell-dismiss-all"
                            onClick={handleDismissAll}
                            type="button"
                        > Dismiss All 
                        </button>
                    </div>

                    {/* Notification Body */}
                    <div className="nbell-panel-body" id="nbell-panel-body">
                        {loading ? (
                            <div className="nbell-loading" id="nbell-loading">
                                <span className="nbell-dot"></span>
                                <span className="nbell-dot"></span>
                                <span className="nbell-dot"></span>
                            </div>
                        ) : (
                            <>
                                {renderSection('Today',        'today',  groups.today)}
                                {renderSection('Last 7 Days',  'last7',  groups.last7)}
                                {renderSection('Last 8-14 Days','last814',groups.last814)}
                            </>
                        )}
                    </div>
                    <div className="nbell-info-banner" id="nbell-info-banner">
                    <div className="nbell-info-banner-left">
                        <span><a id="nbell-dismiss-all" className="nbell-dismiss-all" href="/notifications">View All Notification</a></span>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationBell;